import { ReactNode, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import PageSpinner from '../../../../shared/components/page-spinner/page-spinner';
import { hasPermission } from '../../../../shared/utils/has-permission';
import { useAppDispatch, useAppSelector } from '../../../../store/hooks';
import { openModal } from '../../../ui/store/ui-slice';
import { useGetUserQuery } from '../../../user/api/user-api';
import CurrentUserPasswordModal from '../../../user/components/current-user-password-modal/current-user-password-modal';
import { currentUserPasswordModalId } from '../../../user/constants/constants';
import { UserPermissionType } from '../../../user/models/user-permission-type';
import { setActiveCurrentUserId } from '../../../user/store/current-user-slice';
import { selectIsAuthenticated } from '../../store/auth-slice';
import { setRedirectAfterLoginPath } from '../../utils/redirect-after-login';
import Forbidden from '../forbidden/forbidden';

interface Props {
  children: ReactNode;
  permission?: UserPermissionType;
}

const AuthGuard = ({ children, permission }: Props): JSX.Element => {
  const isAuthenticated = useAppSelector(selectIsAuthenticated);
  const { data: currentUser, isLoading: isCurrentUserLoading } = useGetUserQuery(undefined, {
    skip: !isAuthenticated,
  });
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    if (isAuthenticated && currentUser?.id && currentUser.changePassword && currentUser.canChangePassword) {
      dispatch(
        openModal({
          id: currentUserPasswordModalId,
          props: { isForcedChangePassword: currentUser.changePassword && currentUser.canChangePassword },
        }),
      );
      dispatch(setActiveCurrentUserId(currentUser?.id));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentUser, isAuthenticated]);

  if (!isAuthenticated) {
    const searchParams = new URLSearchParams(location.search);
    const domain = searchParams.get('domain');
    const loginSearch = domain ? `?domain=${encodeURIComponent(domain)}` : '';
    setRedirectAfterLoginPath(location);
    navigate(`/login${loginSearch}`, { replace: true });
    return <></>;
  }

  if (isCurrentUserLoading) {
    return <PageSpinner />;
  }

  if (permission && currentUser && !hasPermission(currentUser, permission)) {
    return <Forbidden />;
  }

  return (
    <>
      {children}
      <CurrentUserPasswordModal />
    </>
  );
};

export default AuthGuard;
